import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDetectAdBlock } from '../helpers/useDetectAdBlock';
import { useLocation } from 'react-router';
import { useUserSubscriptions } from '../queries/SubscriptionQueries';

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

export default function AdComponent() {
  const location = useLocation();
  const { data, isLoading } = useUserSubscriptions();
  const adBlockDetected = useDetectAdBlock();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [currentTimestamp] = useState(() => Date.now());
  const [dismissedAdBlock, setDismissedAdBlock] = useState(() =>
    parseInt(localStorage.getItem('dismissedAdBlock') ?? '0'),
  );

  const isRightTime = useMemo(() => {
    // give 24 hours before asking again
    if (currentTimestamp - dismissedAdBlock > 86400000) {
      return true;
    }
    return false;
  }, [currentTimestamp, dismissedAdBlock]);

  const isLoggedIn = useMemo(() => {
    if (
      location.pathname !== '/' &&
      location.pathname !== '/register' &&
      location.pathname !== '/forgotpassword' &&
      location.pathname !== '/resetpassword' &&
      location.pathname !== '/privacy-policy' &&
      location.pathname !== '/terms-of-use' &&
      !location.pathname.includes('/gallery/')
    ) {
      return true;
    }
    return false;
  }, [location.pathname]);

  const showAds = useMemo(() => {
    if (
      location.pathname === '/' ||
      location.pathname === '/register' ||
      location.pathname === '/forgotpassword' ||
      location.pathname === '/resetpassword' ||
      location.pathname === '/privacy-policy' ||
      location.pathname === '/terms-of-use' ||
      isLoading ||
      data?.isAdFree === true
    ) {
      return false;
    }
    return true;
  }, [location.pathname, data, isLoading]);

  const handleAds = useCallback(() => {
    if (!document.getElementById('adsbygoogleaftermount')) {
      const script = document.createElement('script');
      script.id = 'adsbygoogleaftermount';
      script.type = 'text/javascript';
      script.async = true;
      script.src =
        'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
      document.head.appendChild(script);
    }
    if (showAds) {
      try {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
      } catch {
        console.error('Error loading :');
      }
    }
  }, [showAds]);

  useEffect(() => {
    handleAds();
  }, [handleAds, location.key, showAds]);

  useEffect(() => {
    // Only prompt once subscription status is loaded and confirms ads are enabled.
    if (
      !isLoggedIn ||
      !isRightTime ||
      !adBlockDetected ||
      isLoading ||
      !showAds
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      const dialog = dialogRef.current;
      if (dialog && !dialog.open) dialog.showModal();
    }, 2000);

    return () => window.clearTimeout(timeout);
  }, [
    adBlockDetected,
    showAds,
    isLoggedIn,
    isRightTime,
    location.key,
    isLoading,
  ]);

  const dismissDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
      const timestamp = Date.now();
      localStorage.setItem('dismissedAdBlock', timestamp.toString());
      setDismissedAdBlock(timestamp);
    }
  };

  return (
    <>
      <div key={location.key}>
        <div className='flex justify-center px-2 mt-2'>
          {showAds && (
            <ins
              className='adsbygoogle'
              style={{
                display: 'inline-block',
                width: '300px',
                height: '90px',
              }}
              data-ad-client='ca-pub-9453781066915703'
              data-ad-slot='4308721531'
            ></ins>
          )}
        </div>
      </div>
      <dialog
        ref={dialogRef}
        className='bg-white text-gray-600 m-auto
 dark:bg-gray-800  dark:tracking-wider dark:text-gray-400 '
      >
        <div className='flex h-full items-center justify-center p-2 max-md:flex-col'>
          <img
            src='https://imagedelivery.net/elNH-drmXdXGBUZpB0rC9Q/53aaf874-3aa2-4e20-6b2b-cd21eea24e00/public'
            alt='staring alpaca'
            className='max-w-[200px] rounded-full'
          />
          <div className='flex w-full max-w-[600px] flex-col items-center justify-center p-4'>
            <h3 className='text-center text-2xl dark:text-gray-300'>
              Nobody likes ads. Not even Herc!
            </h3>
            <p>
              But hosting a site costs money, so if it wouldn't be too much to
              ask, could you please disable your ad blocker on this site?
            </p>
            <button
              type='button'
              onClick={() => dismissDialog()}
              className='m-4 max-w-[275px] rounded-md border border-slate-950 bg-transparent p-2 text-xs font-bold uppercase hover:border-gray-400 hover:text-gray-400 dark:border-gray-500 dark:hover:border-gray-300 dark:hover:text-gray-300'
            >
              Continue on to the site
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
